import { chatProtobufs } from '../../protobuf';
import type { ChatCategory } from '../useChats/types';
import type { useChatSocket } from '.';

export type UseChatSocketResult = ReturnType<typeof useChatSocket>;

export interface SendMessageResponse {
  body: {
    statusCode: SendMessageResponseStatusCode;
  };
}

export enum SendMessageResponseStatusCode {
  SUCCESS = 1,
  ERROR = 500,
  NOT_GROUP_MEMBER = 101,
}

export enum ChatSocketControls {
  SEND = 6,
  NOTICE_MSG = 7,
  PULL_IDS = 8,
  PULL_MSG = 9,
  GET_MEDIA_TOKEN = 10,
  MEDIA_OPERATE = 11,
  SYNC_MEDIA_INFO = 12,
  MSG_ARRIVAL = 13,
  MSG_READ = 14,
  PULL_GROUP_MSG_STATUS = 16,
  MEDIA_OPERATE_CALL = 15,
  MEDIA_OPERATE_JOIN = 17,
  GET_MEDIA_INFO = 18,
  DIRECT_PUSH = 20,
}

export enum FlagValue {
  YES = 1,
  NO = 0,
}

export interface ChatSocketFlags {
  retry?: FlagValue; // 10000000
  once?: FlagValue; //   01000000
}

export const reqMap = {
  [ChatSocketControls.SEND]: chatProtobufs.sendMessageRequest,
  [ChatSocketControls.PULL_IDS]: chatProtobufs.pullMessageIdListRequest,
  [ChatSocketControls.PULL_MSG]: chatProtobufs.pullMessageInfoListRequest,
  [ChatSocketControls.GET_MEDIA_TOKEN]: chatProtobufs.getMediaCallTokenRequest,
  [ChatSocketControls.MEDIA_OPERATE]: chatProtobufs.mediaCallOperateRequest,
  [ChatSocketControls.MEDIA_OPERATE_CALL]: chatProtobufs.mediaCallOperateV2Request,
  [ChatSocketControls.MEDIA_OPERATE_JOIN]:
    chatProtobufs.mediaCallOperateCalleeV2Request,
  [ChatSocketControls.SYNC_MEDIA_INFO]: chatProtobufs.sendMessageRequest,
  [ChatSocketControls.GET_MEDIA_INFO]: chatProtobufs.syncMediaCallInfoV2Request,
  [ChatSocketControls.MSG_ARRIVAL]: chatProtobufs.messageArrivalReplyRequest,
  [ChatSocketControls.MSG_READ]: chatProtobufs.messageArrivalReplyRequest,
  [ChatSocketControls.PULL_GROUP_MSG_STATUS]:
    chatProtobufs.pullGroupMsgReadReplyRequest,
} as const;

export const resMap = {
  [ChatSocketControls.SEND]: chatProtobufs.sendMessageResponse,
  [ChatSocketControls.NOTICE_MSG]: chatProtobufs.notice,
  [ChatSocketControls.PULL_IDS]: chatProtobufs.pullMessageIdListResponse,
  [ChatSocketControls.PULL_MSG]: chatProtobufs.pullMessageInfoListResponse,
  [ChatSocketControls.GET_MEDIA_TOKEN]: chatProtobufs.getMediaCallTokenResponse,
  [ChatSocketControls.MEDIA_OPERATE]: chatProtobufs.mediaCallOperateResponse,
  [ChatSocketControls.MEDIA_OPERATE_CALL]: chatProtobufs.mediaCallOperateV2Response,
  [ChatSocketControls.MEDIA_OPERATE_JOIN]:
    chatProtobufs.mediaCallOperateCalleeV2Response,
  [ChatSocketControls.SYNC_MEDIA_INFO]: chatProtobufs.syncMediaCallInfoResponse,
  [ChatSocketControls.GET_MEDIA_INFO]: chatProtobufs.syncMediaCallInfoV2Response,
  [ChatSocketControls.MSG_ARRIVAL]: chatProtobufs.messageArrivalReplyResponse,
  [ChatSocketControls.MSG_READ]: chatProtobufs.readBurnMessageReplyResponse,
  [ChatSocketControls.PULL_GROUP_MSG_STATUS]:
    chatProtobufs.pullGroupMsgReadReplyResponse,
  [ChatSocketControls.DIRECT_PUSH]: chatProtobufs.directPush,
} as const;

export type ReqMap = typeof reqMap;
export type ResMap = typeof resMap;

export interface ChatSocketHeaders<C extends ChatSocketControls> {
  control: C;
  flags?: ChatSocketFlags;
}

export interface ChatSocketMsg<
  C extends ChatSocketControls,
  B extends Record<string, any>,
> {
  headers: ChatSocketHeaders<C>;
  body: B;
}

export type ChatSocketReq<C extends keyof ReqMap> = ChatSocketMsg<
  C,
  Parameters<ReqMap[C]['encode']>[0]
>;

export type ChatSocketRes = ChatSocketMsg<keyof ResMap, Record<string, any>>;

export enum NoticeCode {
  NEW_MSG = 1,
  READ_MSG = 2,
}

export enum PushType {
  SETTINGS = 401,
  EXPIRE_DURATION_NOTICE_SINGLE = 403,
  EXPIRE_DURATION_NOTICE_GROUP = 404,
}

export enum PushAction {
  DEFAULT = 0,
}

export interface Conditions {
  control: ChatSocketControls;
  code?: NoticeCode;
  msgType?: ChatCategory;
  pushAction?: PushAction;
  pushType?: PushType;
}
